import { Router, NavigationEnd } from '@angular/router';
import { ComponentRef, Injector, Injectable, TemplateRef } from '@angular/core';
import { ComponentPortal, ComponentType, TemplatePortal } from 'app/ui/core/portal';
import { UIDialogComponent } from './dialog.component';
import { DialogConfig } from './dialog-config';
import { DialogInjector } from './dialog-injector';
import { Overlay } from 'app/ui/core/overlay';
import { OverlayRef } from 'app/ui/core/overlay-ref';
import { DialogEventEnum } from 'app/ui/dialog/dialog-event.enum';
import { KEY_CODE } from 'app/ui/keyboard/key-codes';
import { filter } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Injectable()
export class UIDialogService {

    private _active: boolean;
    private _overlayRef: OverlayRef;
    dialogEvents: Subject<any> = new Subject(); // If you want to know how the modal window works

    constructor(private _injector: Injector, private _overlay: Overlay, private _router: Router) {
      this._router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => this.close());

      // https://tfs.abbyy.com/HQ/DataCapture/_workitems/edit/62690 (Esc button doesnt close dialogs)
      window.addEventListener('keydown', (e) => this._listenCloseEvent(e));
    }

    open<T>(componentOrTemplateRef: ComponentType<T> | TemplateRef<T>, config?: any): void {
      config = Object.assign({}, new DialogConfig(), config);
        this._makeEvent(DialogEventEnum.open);

        if (this._active) {
            this._destroyDialog();
        }
        this._active = true;

        this._overlayRef = this._createOverlay();
        const dialogContainer = this._attachDialogContainerComponent(config);
        this._attachDialogContent(componentOrTemplateRef, dialogContainer, config);
    }

    close(closeParams: DialogEventEnum = DialogEventEnum.close): void {
        if (this._active) {
          this._makeEvent(closeParams);
          this._destroyDialog();
        }
    }

    private _listenCloseEvent(e: KeyboardEvent) {
      if ((e.which || e.keyCode) === KEY_CODE.ESCAPE) {
        this.close();
      }
    }

    private _makeEvent(event: string) {
      this.dialogEvents.next(event);
    }

    private _destroyDialog() {
        if (this._overlayRef) {
            this._overlayRef.detachBackdrop();
            this._overlayRef.dispose();
        }
    }

    private _createOverlay(): OverlayRef {
        return this._overlay.create({
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-dark-backdrop'
        });
    }

    private _attachDialogContent<T>(componentOrTemplateRef: ComponentType<T> | TemplateRef<T>,
        dialogContainer: UIDialogComponent,
        config: DialogConfig): void {
        // Create a reference to the dialog we're creating in order to give the user a handle
        // to modify and close it.

        // if (!config.disableClose) {
        //     // When the dialog backdrop is clicked, we want to close it.
        //     this.overlayRef.backdropClick().first().subscribe(() => {
        //         // this.result = dialogResult;
        //         this.destroyDialog();
        //     });
        // }

        // We create an injector specifically for the component we're instantiating so that it can
        // inject the DialogRef. This allows a component loaded inside of a dialog to close itself
        // and, optionally, to return a value.
        const userInjector = config && config.viewContainerRef && config.viewContainerRef.injector;
        const dialogInjector = new DialogInjector(userInjector || this._injector, config.data);

        if (componentOrTemplateRef instanceof TemplateRef) {
            dialogContainer.attachTemplatePortal(new TemplatePortal(componentOrTemplateRef, null));
        } else {
            dialogContainer.attachComponentPortal(new ComponentPortal(componentOrTemplateRef, null, dialogInjector));
        }
    }

    private _attachDialogContainerComponent(config: DialogConfig): UIDialogComponent {
        const viewContainer = config ? config.viewContainerRef : null;
        const containerPortal = new ComponentPortal(UIDialogComponent, viewContainer);
        const containerRef: ComponentRef<UIDialogComponent> = this._overlayRef.attach(containerPortal);
        containerRef.instance.dialogConfig = config;

        return containerRef.instance;
    }

}
